<template>
  <div>
    <div class="relative min-h-screen bg-white dark:bg-[#0c1427]/95 ">
    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Page Header -->
      <div class="mb-8">
        <div class="sm:flex sm:items-center sm:justify-between">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ $t('apiSettings.title') }}</h2>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ $t('apiSettings.description') }}</p>
          </div>
          <div class="mt-4 sm:mt-0 flex items-center justify-between sm:justify-start sm:gap-3">
            <a href="/documentation" target="_blank" class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer">
              <i class="ri-book-open-line ltr:mr-2 rtl:ml-2"></i>
              <span class="hidden sm:inline">{{ $t('apiSettings.viewDocumentation') }}</span>
              <span class="sm:hidden">{{ $t('apiSettings.documentation') }}</span>
            </a>
            <button
              @click="showCreateModal = true"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <i class="ri-add-line ltr:mr-2 rtl:ml-2"></i>
              <span class="hidden sm:inline">{{ $t('apiSettings.createNewApiKey') }}</span>
              <span class="sm:hidden">{{ $t('common.create') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- API Keys Table -->
      <div class="bg-white dark:bg-[#15203c] shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">{{ $t('apiSettings.yourApiKeys') }}</h3>
        </div>
        
        <!-- Desktop Table View -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-white dark:bg-[#15203c] border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {{ $t('apiSettings.name') }}
                </th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {{ $t('apiSettings.last4Digits') }}
                </th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {{ $t('apiSettings.creationDate') }}
                </th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {{ $t('apiSettings.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-[#15203c] divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-if="loading">
                <td colspan="4" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                  <div class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ $t('common.loading') }}...
                  </div>
                </td>
              </tr>
              <template v-else>
                <tr
                  v-for="(key, index) in apiKeys"
                  :key="key.id"
                  class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td class="px-4 py-3 whitespace-nowrap">
                    <div class="flex items-center justify-center">
                      <div class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ltr:mr-3 rtl:ml-3"
                           :class="getKeyIconClass(index)">
                        <i class="ri-key-line text-sm"></i>
                      </div>
                      <div class="text-center">
                        <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ key.name }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ $t('apiSettings.apiKeyDescription') }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-center">
                    <div class="inline-block text-sm font-mono text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded">
                      •••• {{ key.last_chars }}
                    </div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                    {{ formatDate(key.created_at) }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-center">
                    <button
                      @click="confirmDelete(key)"
                      class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      :title="$t('common.delete')"
                    >
                      <i class="ri-delete-bin-line"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="apiKeys.length === 0">
                  <td colspan="4" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                    {{ $t('apiSettings.noApiKeys') }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Mobile Cards View -->
        <div class="sm:hidden space-y-4 p-4">
          <div v-if="loading" class="text-center py-8">
            <div class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ $t('common.loading') }}...
            </div>
          </div>
          
          <template v-else>
            <div
              v-for="(key, index) in apiKeys"
              :key="key.id"
              class="bg-white dark:bg-[#15203c] border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm"
            >
              <div class="flex items-start justify-between">
                <div class="flex items-start space-x-3">
                  <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                       :class="getKeyIconClass(index)">
                    <i class="ri-key-line text-lg"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ key.name }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ $t('apiSettings.apiKeyDescription') }}</div>
                    <div class="text-xs font-mono text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2 inline-block">
                      •••• {{ key.last_chars }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {{ formatDate(key.created_at) }}
                    </div>
                  </div>
                </div>
                <button
                  @click="confirmDelete(key)"
                  class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  :title="$t('common.delete')"
                >
                  <i class="ri-delete-bin-line text-lg"></i>
                </button>
              </div>
            </div>
            
            <div v-if="apiKeys.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
              {{ $t('apiSettings.noApiKeys') }}
            </div>
          </template>
        </div>
        
        <!-- Desktop Footer -->
        <div v-if="apiKeys.length > 0" class="hidden sm:block px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-[#15203c]">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-500 dark:text-gray-400">
              {{ $t('apiSettings.showingApiKeys', { count: apiKeys.length }) }}
            </div>
            <div class="text-sm text-gray-500 dark:text-gray-400">
              <i class="ri-information-line ltr:mr-1 rtl:ml-1"></i>
              {{ $t('apiSettings.securityWarning') }}
            </div>
          </div>
        </div>

        <!-- Mobile Footer -->
        <div v-if="apiKeys.length > 0" class="sm:hidden px-4 py-3 bg-white dark:bg-[#15203c] border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <div class="text-center space-y-2">
            <div class="text-sm text-gray-500 dark:text-gray-400">
              {{ $t('apiSettings.showingApiKeys', { count: apiKeys.length }) }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center">
              <i class="ri-information-line ltr:mr-1 rtl:ml-1"></i>
              {{ $t('apiSettings.securityWarning') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Help Section -->
      <div class="mt-8 bg-white dark:bg-[#15203c] border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <i class="ri-lightbulb-line text-blue-600 dark:text-blue-400 text-lg"></i>
          </div>
          <div class="ltr:ml-3 rtl:mr-3">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ $t('apiSettings.needHelp') }}</h3>
            <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <p>{{ $t('apiSettings.helpDescription') }}</p>
            </div>
            <div class="mt-3">
              <a href="/documentation" target="_blank" class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer">
                <i class="ri-external-link-line ltr:mr-1 rtl:ml-1"></i>
                {{ $t('apiSettings.readApiDocumentation') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>
    </div>

    <!-- Create API Key Modal -->
    <div
      v-if="showCreateModal"
      class="modal"
      @click.self="showCreateModal = false"
    >
      <div class="bg-white dark:bg-[#15203c] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full max-h-screen overflow-y-auto">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <i class="ri-key-line text-blue-600 dark:text-blue-400"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ $t('apiSettings.createNewApiKey') }}</h3>
            </div>
            <button
              @click="showCreateModal = false"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <i class="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
        
        <!-- Modal Body -->
        <div class="px-6 py-6">
          <div class="mb-6">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              {{ $t('apiSettings.keyName') }}
            </label>
            <div class="relative">
              <input
                v-model="newKeyName"
                type="text"
                class="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 text-sm transition-colors"
                :placeholder="$t('apiSettings.keyNamePlaceholder')"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <i class="ri-edit-line text-gray-400"></i>
              </div>
            </div>
            <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {{ $t('apiSettings.keyNameHelper') }}
            </p>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="px-6 py-4 bg-white dark:bg-[#15203c]/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
          <div class="flex justify-between items-center">
            <button
              @click="showCreateModal = false"
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded-lg transition-colors"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              @click="createApiKey"
              :disabled="!newKeyName.trim() || loading"
              class="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors flex items-center"
            >
              <span v-if="loading" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ $t('common.loading') }}
              </span>
              <span v-else class="flex items-center">
                <i class="ri-add-line ltr:mr-2 rtl:ml-2"></i>
                {{ $t('common.create') }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Generated API Key Modal -->
    <div
      v-if="showKeyModal"
      class="modal"
    >
      <div class="bg-white dark:bg-[#15203c] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full max-h-screen overflow-y-auto">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <i class="ri-check-line text-green-600 dark:text-green-400"></i>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ $t('apiSettings.apiKeyCreated') }}</h3>
          </div>
        </div>
        
        <!-- Modal Body -->
        <div class="px-6 py-6">
          <!-- Warning Alert -->
          <div class="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <i class="ri-alert-line text-orange-600 dark:text-orange-400 text-lg"></i>
              </div>
              <div class="ltr:ml-3 rtl:mr-3">
                <h4 class="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-1">
                  {{ $t('apiSettings.important') }}
                </h4>
                <p class="text-sm text-orange-700 dark:text-orange-300">
                  {{ $t('apiSettings.saveKeyWarning') }}
                </p>
              </div>
            </div>
          </div>

          <!-- API Key Display -->
          <div class="mb-6">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              {{ $t('apiSettings.yourNewApiKey') }}
            </label>
            <div class="relative">
              <input
                :value="generatedKey"
                type="text"
                readonly
                class="w-full px-4 py-3 pr-12 font-mono text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 select-all"
              />
              <button
                @click="copyKey"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                :title="$t('common.copy')"
              >
                <div class="flex items-center space-x-1">
                  <i :class="copied ? 'ri-check-line text-green-600 dark:text-green-400' : 'ri-file-copy-line'"></i>
                  <span v-if="copied" class="text-xs text-green-600 dark:text-green-400">{{ $t('common.copied') }}</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="px-6 py-4 bg-gray-50 dark:bg-[#15203c]/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
          <div class="flex justify-end">
            <button
              @click="closeKeyModal"
              class="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors flex items-center"
            >
              <i class="ri-check-line ltr:mr-2 rtl:ml-2"></i>
              {{ $t('common.done') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="modal"
      @click.self="showDeleteModal = false"
    >
      <div class="bg-white dark:bg-[#15203c] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full max-h-screen overflow-y-auto">
        <div class="p-6">
          <h3 class="text-lg font-semibold mb-4">{{ $t('apiSettings.deleteConfirmTitle') }}</h3>
          
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            {{ $t('apiSettings.deleteConfirmMessage', { name: keyToDelete?.name }) }}
          </p>

          <div class="flex justify-between items-center">
            <button
              @click="showDeleteModal = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              @click="deleteApiKey"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <span v-if="loading" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ $t('common.loading') }}
              </span>
              <span v-else>{{ $t('common.delete') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { supabase } from "@/utils/supabase";
import { profileService } from "@/utils/supabase";
import PageTitle from "@/components/Common/PageTitle.vue";
import emitter from "@/utils/emitter";

interface ApiKey {
  id: number;
  name: string;
  last_chars: string;
  created_at: string;
  organization_id: string;
}

export default defineComponent({
  name: "ApiSettingsPage",
  setup() {
    const { t, locale } = useI18n();
    const apiKeys = ref<ApiKey[]>([]);
    const showCreateModal = ref(false);
    const showKeyModal = ref(false);
    const showDeleteModal = ref(false);
    const newKeyName = ref("");
    const generatedKey = ref("");
    const keyToDelete = ref<ApiKey | null>(null);
    const copied = ref(false);
    const loading = ref(false);
    const organizationId = ref<string | null>(null);



    // Get current user's organization
    const getCurrentOrganization = async () => {
      try {
        const { data: profile, error } = await profileService.getCurrentProfile();
        if (error || !profile) {
          console.error('Error getting profile:', error);
          return null;
        }
        return profile.org_id;
      } catch (error) {
        console.error('Error getting organization:', error);
        return null;
      }
    };

    const loadApiKeys = async () => {
      try {
        loading.value = true;
        
        // Get organization ID
        const orgId = await getCurrentOrganization();
        if (!orgId) {
          console.error('Error getting organization');
          return;
        }
        
        organizationId.value = orgId;

        const { data, error } = await supabase
          .from('api_keys')
          .select('*')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading API keys:', error);
          return;
        }

        apiKeys.value = data as any || [];
      } catch (error) {
        console.error('Error loading API keys:', error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(locale.value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    const createApiKey = async () => {
      if (!newKeyName.value.trim()) return;

      try {
        loading.value = true;

        if (!organizationId.value) {
          console.error('Error getting organization');
          return;
        }

        // Send request to API
        const response = await fetch('https://api-v2.clickai.live/create-api-key', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            organization_id: organizationId.value,
            name: newKeyName.value.trim(),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || 'Failed to create API key');
        }

        // Store the generated key to show to user
        generatedKey.value = result.api_key;
        
        // Add to local list for display
        const newApiKeyData = {
          id: Date.now(), // Temporary ID for display
          name: result.name,
          last_chars: result.last_chars,
          created_at: new Date().toISOString(),
          organization_id: result.organization_id,
        };
        
        apiKeys.value.unshift(newApiKeyData);

        // Reset form and show key modal
        newKeyName.value = "";
        showCreateModal.value = false;
        showKeyModal.value = true;
      } catch (error) {
        console.error('Error creating API key:', error);
        // You might want to show an error message to the user here
      } finally {
        loading.value = false;
      }
    };

    const copyKey = async () => {
      try {
        await navigator.clipboard.writeText(generatedKey.value);
        copied.value = true;
        // Key copied successfully
        setTimeout(() => {
          copied.value = false;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
        // Copy failed
      }
    };

    const closeKeyModal = () => {
      showKeyModal.value = false;
      generatedKey.value = "";
    };

    const confirmDelete = (key: ApiKey) => {
      keyToDelete.value = key;
      showDeleteModal.value = true;
    };

    const deleteApiKey = async () => {
      if (!keyToDelete.value) return;

      try {
        loading.value = true;

        const { error } = await supabase
          .from('api_keys')
          .delete()
          .eq('id', keyToDelete.value.id);

        if (error) {
          console.error('Error deleting API key:', error);
          return;
        }

        // Remove from local list
        apiKeys.value = apiKeys.value.filter(k => k.id !== keyToDelete.value!.id);
        
        showDeleteModal.value = false;
        keyToDelete.value = null;
      } catch (error) {
        console.error('Error deleting API key:', error);
      } finally {
        loading.value = false;
      }
    };

    const getKeyIconClass = (index: number) => {
      const colors = [
        'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
        'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400',
        'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
        'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400',
        'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
        'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
      ];
      return colors[index % colors.length];
    };

    const viewApiKey = (key: ApiKey) => {
      // This function can be expanded to show API key details
      console.log('Viewing API key:', key);
      // You can add a modal here to show more details about the API key
    };

    // Setup refresh event listener
    const setupRefreshHandler = () => {
      emitter.on('page:refresh:api-settings', () => {
        console.log('Refreshing API settings data');
        loadApiKeys();
      });
    };

    const cleanupRefreshHandler = () => {
      emitter.off('page:refresh:api-settings');
    };

    onMounted(() => {
      loadApiKeys();
      setupRefreshHandler();
    });

    onUnmounted(() => {
      cleanupRefreshHandler();
    });

    return {
      apiKeys,
      showCreateModal,
      showKeyModal,
      showDeleteModal,
      newKeyName,
      generatedKey,
      keyToDelete,
      copied,
      loading,
      formatDate,
      createApiKey,
      copyKey,
      closeKeyModal,
      confirmDelete,
      deleteApiKey,
      getKeyIconClass,
      viewApiKey,
    };
  },
});
</script>

<style scoped>
/* Add any custom styles if needed */
</style> 